import Hue from '@assets/hue_hero.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useRef } from 'react';

interface Props {
  readonly anchorId?: string;
  readonly bottomImage: IImage;
  readonly link: ILink;
  readonly title: string;
  readonly topImage: IImage;
}

const ParallaxHero = ({
  anchorId = '',
  bottomImage,
  link,
  title,
  topImage,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    offset: ['start end', 'end start'],
    target: ref,
  });
  const { scrollYProgress: pageScrollYProgress } = useScroll();
  const isMobileView = useMediaQuery(767);

  const y1 = useTransform(
    scrollYProgress,
    [0, 1],
    isMobileView ? [-50, 50] : [-150, 150]
  );
  const y2 = useTransform(
    scrollYProgress,
    [0, 1],
    isMobileView ? [25, -75] : [100, -250]
  );
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 0.9]);

  const transform = useTransform(pageScrollYProgress, [0, 1], ['0%', '-100%']);
  const physics = {
    damping: 15,
    mass: 0.27,
    stiffness: 55,
  };
  const spring = useSpring(transform, physics);

  return (
    <section
      className="flex min-h-[50vh] overflow-hidden bg-black-100 py-15 text-white lg:min-h-[95vh]"
      id={`a-${anchorId}`}
      ref={ref}
      data-testid="CT-04-E-Parallax-Hero"
    >
      <motion.div
        className="container flex flex-col items-stretch lg:grid lg:grid-cols-12 lg:items-center"
        initial={{
          opacity: 0,
          y: 50,
        }}
        style={{ y: spring }}
        viewport={{ once: true }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        data-testid="CT-04-E-Parallax-Hero-Container"
      >
        <div className="grow md:col-span-6 md:col-start-1 xl:col-span-5">
          <h1
            dangerouslySetInnerHTML={{ __html: title }}
            data-testid="CT-04-E-Parallax-Hero-Title"
          />
          {link?.url && link?.name && (
            <Button
              className="mt-11 text-center lg:mt-16"
              link={link}
              dataTestId="CT-04-E-Parallax-Hero-Link"
            />
          )}
        </div>
        <div className="relative flex grow items-center pt-16 md:col-span-7 md:col-start-7 lg:pt-0 xl:col-span-7 xl:col-start-6">
          <motion.div
            className="pointer-events-none absolute left-0 top-0 z-10 flex w-full items-center justify-center md:-top-16"
            style={{ y: y1 }}
          >
            <img
              alt="Hue"
              className="h-full w-full scale-x-[200%] scale-y-150"
              src={`${Hue}`}
              data-testid="CT-04-E-Parallax-Hero-Hue"
            />
          </motion.div>
          <motion.div
            className="relative z-20 w-full"
            style={{
              scale,
              y: y1,
            }}
          >
            <Image
              image={topImage}
              imageClasses="h-full w-full object-contain"
              sizes="(min-width: 1024px) 1024px, 100vw"
              dataTestId="CT-04-E-Parallax-Hero-Top-Image"
            />
          </motion.div>
          <motion.div
            className="absolute z-10 w-full"
            style={{
              scale,
              y: y2,
            }}
          >
            <Image
              image={bottomImage}
              imageClasses="h-full w-full object-contain"
              sizes="(min-width: 1024px) 1024px, 100vw"
              dataTestId="CT-04-E-Parallax-Hero-Bottom-Image"
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default ParallaxHero;
